import { useLoaderData } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { PILLAR_COLORS } from '@/data/blogPillars';
import { AnimatedSection } from '@/hooks/useScrollAnimation';
import { Button } from '@/components/ui/button';
import { useBlogDestaques, type BlogPost } from '@/hooks/useBlogPosts';
import { capaDoPost } from '@/data/capasDoBlog';

const BlogHighlights = () => {
  // Os 3 posts vem do loader da rota "/" (src/App.tsx), entao esta secao ja
  // nasce dentro do HTML estatico. Sem isso ela caia no `return null` abaixo
  // durante a geracao e so aparecia depois da hidratacao, empurrando o rodape.
  const dadosIniciais = useLoaderData() as BlogPost[] | undefined;
  const { data: posts } = useBlogDestaques(dadosIniciais);

  if (!posts || posts.length === 0) return null;

  return (
    <AnimatedSection className="section-padding bg-background" id="blog">
      <div className="container-mobile">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Blog
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-3">
            Do nosso blog
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Conteúdo clínico para entender sua saúde de verdade
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => {
            const pillarStyle = post.pillar ? PILLAR_COLORS[post.pillar] : null;
            return (
              <a
                key={post.id}
                href={`/blog/${post.slug}`}
                className="group block bg-card border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
              >
                {capaDoPost(post.slug, post.cover_image_url) && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={capaDoPost(post.slug, post.cover_image_url)!.src}
                      srcSet={capaDoPost(post.slug, post.cover_image_url)!.srcSet}
                      sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw"
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      width={1200}
                      height={675}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="p-5 md:p-6">
                  {pillarStyle && post.pillar && (
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${pillarStyle.bg} ${pillarStyle.text}`}>
                      {post.pillar}
                    </span>
                  )}
                  <h3 className="font-serif text-lg md:text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                  )}
                  {post.reading_time && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5" />
                      {post.reading_time} min de leitura
                    </span>
                  )}
                </div>
              </a>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Button asChild variant="outline" size="lg">
            <a href="/blog">
              Ver todos os artigos
              <ArrowRight className="w-4 h-4 ml-1" />
            </a>
          </Button>
        </div>
      </div>
    </AnimatedSection>
  );
};

export default BlogHighlights;
